// Helpers for images stored in the public R2 bucket.
//
// Unlike Cloudinary, R2 can't transform on the fly — the server pre-generates
// a fixed ladder of widths in AVIF, WebP and one JPEG fallback, all under a
// shared base URL. These helpers turn that base into concrete URLs.
//
// Must stay in sync with WIDTHS / FALLBACK_WIDTH in server/lib/images.js.
export const R2_IMAGE_WIDTHS = [400, 800, 1600] as const;
export const R2_FALLBACK_WIDTH = 800;

export type ImageFormat = 'avif' | 'webp' | 'jpg';

/**
 * An R2 image base looks like `https://img.example.com/images/name-abc123`
 * — a path segment under /images/ with no file extension. Cloudinary URLs and
 * ordinary file URLs both have extensions, so this stays unambiguous.
 */
export const isR2ImageBase = (value?: string | null): boolean =>
  Boolean(value && value.includes('/images/') && !/\.[a-z0-9]{2,5}$/i.test(value));

/**
 * Recovers the base from a concrete variant URL, e.g. the `800.jpg` link the
 * editor embeds in blog bodies. Returns null if it isn't one of ours.
 */
export const baseFromVariantUrl = (url?: string | null): string | null => {
  if (!url) return null;
  const match = url.match(/^(.*\/images\/[^/]+)\/\d+\.(?:avif|webp|jpg)$/i);
  return match ? match[1] : null;
};

export const r2VariantUrl = (base: string, width: number, format: ImageFormat): string =>
  `${base}/${width}.${format}`;

// The server skips widths larger than the source, so a variant may not exist.
// Requesting the nearest available width keeps srcset honest.
export const r2SrcSet = (base: string, format: ImageFormat, widths: readonly number[] = R2_IMAGE_WIDTHS): string =>
  widths.map((w) => `${r2VariantUrl(base, w, format)} ${w}w`).join(', ');

export const r2FallbackUrl = (base: string): string =>
  r2VariantUrl(base, R2_FALLBACK_WIDTH, 'jpg');
